import { ErrorBoundary, Provider as RollbarProvider } from '@rollbar/react';
import { ConfigProvider } from 'antd';
import { appWithTranslation } from 'next-i18next/pages';
import { PostHogProvider } from 'posthog-js/react';
import { useMemo } from 'react';

import { ClientRouteGuard } from '@/components/templates/ClientRouteGuard';
import { AuthProvider } from '@/context/auth.context';
import { NewFiltersProvider } from '@/context/filters.context/Filters.context';
import { ModalProvider } from '@/context/modal.context';
import { ProfileSettingsProvider } from '@/context/profileSettings.context';
import { ToastProvider } from '@/context/toast.context';
import { FiltersProvider } from '@/context/useFilters/FiltersContext';
import { usePageTranslations } from '@/hooks/usePageTranslations';
import { AntdRegistry } from '@/lib/antdRegistry';
import { getPersistenceSetting } from '@/lib/cookies';
import type { NextPageWithNamespaces } from '@/lib/i18n/page.types';
import { applyPosthogEnvironmentCapture } from '@/lib/posthogCapture';
import rollbarClient from '@/lib/rollbar/rollbarClient';
import { roboto } from '@/styles/fonts';
import type { SupportedLanguage } from '@/utils/globalTypes';

import * as nextI18nextConfig from '../../next-i18next.config';

import type { AppProps } from 'next/app';

import '@/styles/globals.css';
import 'ol/ol.css'; // neded for geofluxus-map
import 'sanitize.css';

interface ExtendedAppProps extends AppProps {
  Component: NextPageWithNamespaces;
  pageProps: AppProps['pageProps'];
}

function App({ Component, pageProps }: ExtendedAppProps) {
  const bootstrapLang = (pageProps.bootstrapLang ?? 'en') as SupportedLanguage;
  const initialAuth = pageProps.initialAuth ?? null;

  const namespacesRequired = useMemo(() => Component.namespacesRequired ?? [], [Component]);
  const i18nReady = usePageTranslations(namespacesRequired);

  return (
    <AntdRegistry>
      <ConfigProvider
        theme={{
          cssVar: {},
          hashed: process.env.NODE_ENV === 'production' ? true : false,
          token: {
            borderRadius: 2,
            borderRadiusLG: 2,
            borderRadiusSM: 2,
            colorPrimary: '#152bee',
            colorTextBase: '#313639d9',
            colorLink: 'white',
            colorBgLayout: '#fcfcfd',
            fontFamily: roboto.style.fontFamily,
            fontSize: 14,
            marginLG: 16,
            marginMD: 12,
            marginSM: 6,
            marginXS: 4,
            marginXXS: 2,
            colorText: '#1d2939',
            colorTextHeading: '#1d2939',
            colorTextSecondary: '#475467',
            colorTextDescription: '#475467',
          },
          components: {
            Card: {
              paddingLG: 0,
            },
            Button: {
              colorLink: '#152bee',
              colorLinkHover: '#5c81f5',
              colorBgContainerDisabled: '#f2f4f7',
              borderRadius: 4,
              borderRadiusSM: 4,
              borderRadiusXS: 2,
              contentFontSizeSM: 12,
              primaryShadow: 'none',
            },
            Radio: {
              buttonCheckedBg: '#f0f6ff',
              buttonColor: '#667085',
              colorBgContainer: 'transparent',
            },
            Checkbox: {
              colorBgContainer: 'gainsboro',
            },
            Segmented: {
              itemSelectedColor: '#152bee',
            },
            Menu: {
              itemHoverBg: 'transparent',
              itemSelectedBg: '#f7faff',
              itemSelectedColor: '#080f5e',
            },
            Typography: {
              fontWeightStrong: 500,
            },
            Tag: {
              blue1: '#f0f6ff',
              blue7: '#152bee',
              defaultBg: '#f2f4f7',
              defaultColor: '#1d2939',
            },
            Select: {
              colorBorder: '#eaecf0',
            },
          },
        }}
      >
        <RollbarProvider instance={rollbarClient}>
          <PostHogProvider
            apiKey={process.env.NEXT_PUBLIC_POSTHOG_KEY!}
            options={{
              api_host: 'https://eu.i.posthog.com',
              defaults: '2026-01-30',
              capture_pageview: 'history_change',
              enable_heatmaps: true,
              persistence: getPersistenceSetting(),
              loaded: (ph) => {
                applyPosthogEnvironmentCapture(ph);
              },
            }}
          >
            <ErrorBoundary>
              <AuthProvider initialAuth={initialAuth}>
                <ProfileSettingsProvider bootstrapLang={bootstrapLang}>
                  <FiltersProvider>
                    <NewFiltersProvider>
                      <ToastProvider>
                        <ModalProvider>
                          <ClientRouteGuard>{i18nReady ? <Component {...pageProps} /> : null}</ClientRouteGuard>
                        </ModalProvider>
                      </ToastProvider>
                    </NewFiltersProvider>
                  </FiltersProvider>
                </ProfileSettingsProvider>
              </AuthProvider>
            </ErrorBoundary>
          </PostHogProvider>
        </RollbarProvider>
      </ConfigProvider>
    </AntdRegistry>
  );
}

export default appWithTranslation(App, nextI18nextConfig);
